import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, User, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";

export default function Students() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const levelMode = levelSystemOf(user);
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["students", q],
    queryFn: async () =>
      unwrap(await api.get("/students", { params: q ? { q } : {} })),
  });

  return (
    <div className="p-6 lg:p-8" data-testid="students-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("management.studentsTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("management.studentsSubtitle")}
        </p>
      </div>

      <div className="relative max-w-md mb-4">
        <Search className="h-4 w-4 text-[var(--text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          className="pl-9"
          placeholder={t("management.studentsSearch")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="student-search"
        />
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">Adm No.</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">{levelWord(levelMode, language)}</th>
                  <th className="text-left px-4 py-3">Gender</th>
                  <th className="text-left px-4 py-3">Section</th>
                  <th className="text-left px-4 py-3">Admission Date</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">View details</th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map((s) => (
                  <tr
                    key={s.id}
                    className="hover:bg-[var(--bg)] transition-colors"
                    data-testid={`student-row-${s.admission_no}`}
                  >
                    <td className="px-4 py-3 font-mono-num text-xs">
                      {s.admission_no}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/students/${s.id}`}
                        className="font-medium hover:text-[var(--accent)]"
                      >
                        {s.first_name} {s.last_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`student-level-${s.admission_no}`}>
                      {levelDisplayName(s.current_enrollment?.grade, language)}
                    </td>
                    <td className="px-4 py-3 capitalize text-xs">{s.gender}</td>
                    <td className="px-4 py-3 text-xs">
                      {s.current_enrollment?.section?.name || (s.current_enrollment?.section_id ? "Assigned" : "—")}
                    </td>
                    <td className="px-4 py-3 font-mono-num text-xs">
                      {formatDate(s.admission_date)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className="bg-[var(--success-bg)] text-[var(--success)] border-0 capitalize">
                        {s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        asChild
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        title={`View details for ${s.first_name} ${s.last_name}`}
                      >
                        <Link
                          to={`/students/${s.id}`}
                          aria-label={`View details for ${s.first_name} ${s.last_name}`}
                          data-testid={`view-student-${s.admission_no}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
                {(data || []).length === 0 && !isLoading && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-10 text-[var(--text-muted)]"
                    >
                      {t("management.studentsNoResults")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
